import React, { useState, useEffect } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { Lock, Mail, Calendar, Trash2, Eye, Settings, LogOut, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import SEO from '../components/SEO';

const Admin = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [contacts, setContacts] = useState([]);
  const [scheduling, setScheduling] = useState([]);
  const [authHeader, setAuthHeader] = useState('');
  
  // Password change state
  const [showPasswordDialog, setShowPasswordDialog] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  
  // View details state
  const [selectedItem, setSelectedItem] = useState(null);
  const [showDetailsDialog, setShowDetailsDialog] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, password }),
      });
      
      if (response.ok) {
        const auth = btoa(`${username}:${password}`);
        setAuthHeader(`Basic ${auth}`);
        setIsAuthenticated(true);
        toast.success('Login successful');
        fetchData(`Basic ${auth}`);
      } else {
        toast.error('Invalid credentials');
      }
    } catch (error) {
      toast.error('Login failed');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchData = async (auth) => {
    try {
      const [contactsRes, schedulingRes] = await Promise.all([
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/contacts`, {
          headers: { 'Authorization': auth }
        }),
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/scheduling`, {
          headers: { 'Authorization': auth }
        })
      ]);
      
      if (contactsRes.ok) {
        const contactsData = await contactsRes.json();
        setContacts(contactsData);
      }
      if (schedulingRes.ok) {
        const schedulingData = await schedulingRes.json();
        setScheduling(schedulingData);
      }
    } catch (error) {
      toast.error('Failed to fetch data');
    }
  };

  const handleStatusChange = async (type, id, newStatus) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/${type}/${id}`,
        {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': authHeader
          },
          body: JSON.stringify({ status: newStatus })
        }
      );
      
      if (response.ok) {
        toast.success('Status updated');
        fetchData(authHeader);
      } else {
        toast.error('Failed to update status');
      }
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const handleDelete = async (type, id) => {
    if (!window.confirm('Are you sure you want to delete this submission?')) return;
    
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/${type}/${id}`,
        {
          method: 'DELETE',
          headers: { 'Authorization': authHeader }
        }
      );
      
      if (response.ok) {
        toast.success('Deleted successfully');
        fetchData(authHeader);
      } else {
        toast.error('Failed to delete');
      }
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    
    if (newPassword !== confirmPassword) {
      toast.error('New passwords do not match');
      return;
    }
    
    if (newPassword.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/change-password`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': authHeader
          },
          body: JSON.stringify({
            current_password: currentPassword,
            new_password: newPassword
          })
        }
      );
      
      if (response.ok) {
        toast.success('Password changed successfully');
        setShowPasswordDialog(false);
        setCurrentPassword('');
        setNewPassword('');
        setConfirmPassword('');
        // Update auth header with new password
        const auth = btoa(`admin:${newPassword}`);
        setAuthHeader(`Basic ${auth}`);
      } else {
        const data = await response.json();
        toast.error(data.detail || 'Failed to change password');
      }
    } catch (error) {
      toast.error('Failed to change password');
    }
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
    setAuthHeader('');
    setPassword('');
    setContacts([]);
    setScheduling([]);
    toast.success('Logged out');
  };

  const getStatusBadge = (status) => {
    const variants = {
      new: 'bg-blue-100 text-blue-800',
      pending: 'bg-yellow-100 text-yellow-800',
      contacted: 'bg-green-100 text-green-800',
      scheduled: 'bg-purple-100 text-purple-800',
      completed: 'bg-gray-100 text-gray-800',
      cancelled: 'bg-red-100 text-red-800',
    };
    return (
      <Badge className={variants[status] || 'bg-gray-100 text-gray-800'}>
        {status}
      </Badge>
    );
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-sage-50 to-taupe-50 p-4">
        <SEO 
          title="Admin Login | Mind Games Family Counseling"
          description="Admin dashboard login"
        />
        <Card className="w-full max-w-md shadow-xl">
          <CardHeader className="text-center">
            <div className="mx-auto w-16 h-16 bg-sage-100 rounded-full flex items-center justify-center mb-4">
              <Lock className="w-8 h-8 text-sage-600" />
            </div>
            <CardTitle className="text-2xl">Admin Login</CardTitle>
            <CardDescription>Enter your credentials to access the dashboard</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleLogin} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="username">Username</Label>
                <Input
                  id="username"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  placeholder="admin"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="password">Password</Label>
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter password"
                  required
                />
              </div>
              <Button 
                type="submit" 
                className="w-full bg-sage-600 hover:bg-sage-700"
                disabled={isLoading}
              >
                {isLoading ? 'Logging in...' : 'Login'}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-sage-50 to-taupe-50">
      <SEO 
        title="Admin Dashboard | Mind Games Family Counseling"
        description="Admin dashboard"
      />
      
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-taupe-200">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
            <h1 className="text-xl sm:text-2xl font-bold text-gray-800">Admin Dashboard</h1>
            <div className="flex items-center gap-2 sm:gap-4 flex-wrap">
              <Button
                variant="outline"
                size="sm"
                onClick={() => fetchData(authHeader)}
                className="border-sage-300"
              >
                <RefreshCw className="w-4 h-4 sm:mr-2" />
                <span className="hidden sm:inline">Refresh</span>
              </Button>
              <Dialog open={showPasswordDialog} onOpenChange={setShowPasswordDialog}>
                <DialogTrigger asChild>
                  <Button variant="outline" size="sm" className="border-sage-300">
                    <Settings className="w-4 h-4 sm:mr-2" />
                    <span className="hidden sm:inline">Change Password</span>
                  </Button>
                </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Change Password</DialogTitle>
                  <DialogDescription>
                    Enter your current password and choose a new one.
                  </DialogDescription>
                </DialogHeader>
                <form onSubmit={handlePasswordChange} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="current">Current Password</Label>
                    <Input
                      id="current"
                      type="password"
                      value={currentPassword}
                      onChange={(e) => setCurrentPassword(e.target.value)}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="new">New Password</Label>
                    <Input
                      id="new"
                      type="password"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      required
                      minLength={8}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="confirm">Confirm New Password</Label>
                    <Input
                      id="confirm"
                      type="password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      required
                    />
                  </div>
                  <DialogFooter>
                    <Button type="submit" className="bg-sage-600 hover:bg-sage-700">
                      Update Password
                    </Button>
                  </DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
            <Button 
              variant="outline" 
              size="sm" 
              onClick={handleLogout}
              className="border-red-300 text-red-600 hover:bg-red-50"
            >
              <LogOut className="w-4 h-4 sm:mr-2" />
              <span className="hidden sm:inline">Logout</span>
            </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 py-4 sm:py-8">
        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4 mb-6 sm:mb-8">
          <Card>
            <CardContent className="p-4 sm:pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs sm:text-sm text-gray-500">Total Contacts</p>
                  <p className="text-2xl sm:text-3xl font-bold text-sage-600">{contacts.length}</p>
                </div>
                <Mail className="w-8 h-8 sm:w-10 sm:h-10 text-sage-200" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 sm:pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs sm:text-sm text-gray-500">New Contacts</p>
                  <p className="text-2xl sm:text-3xl font-bold text-blue-600">
                    {contacts.filter(c => c.status === 'new').length}
                  </p>
                </div>
                <Mail className="w-8 h-8 sm:w-10 sm:h-10 text-blue-200" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 sm:pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs sm:text-sm text-gray-500">Scheduling</p>
                  <p className="text-2xl sm:text-3xl font-bold text-taupe-600">{scheduling.length}</p>
                </div>
                <Calendar className="w-8 h-8 sm:w-10 sm:h-10 text-taupe-200" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 sm:pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs sm:text-sm text-gray-500">Pending</p>
                  <p className="text-2xl sm:text-3xl font-bold text-yellow-600">
                    {scheduling.filter(s => s.status === 'pending').length}
                  </p>
                </div>
                <Calendar className="w-8 h-8 sm:w-10 sm:h-10 text-yellow-200" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <Tabs defaultValue="contacts" className="space-y-4">
          <TabsList className="bg-white border border-taupe-200 w-full sm:w-auto">
            <TabsTrigger value="contacts" className="data-[state=active]:bg-sage-100 flex-1 sm:flex-none text-xs sm:text-sm">
              <Mail className="w-4 h-4 mr-1 sm:mr-2" />
              <span className="hidden sm:inline">Contact Messages</span>
              <span className="sm:hidden">Contacts</span>
              <span className="ml-1">({contacts.length})</span>
            </TabsTrigger>
            <TabsTrigger value="scheduling" className="data-[state=active]:bg-sage-100 flex-1 sm:flex-none text-xs sm:text-sm">
              <Calendar className="w-4 h-4 mr-1 sm:mr-2" />
              <span className="hidden sm:inline">Scheduling Requests</span>
              <span className="sm:hidden">Scheduling</span>
              <span className="ml-1">({scheduling.length})</span>
            </TabsTrigger>
          </TabsList>

          {/* Contacts Tab */}
          <TabsContent value="contacts">
            <Card>
              <CardHeader className="pb-2 sm:pb-6">
                <CardTitle className="text-lg sm:text-xl">Contact Form Submissions</CardTitle>
                <CardDescription>View and manage contact inquiries</CardDescription>
              </CardHeader>
              <CardContent>
                {contacts.length === 0 ? (
                  <p className="text-center text-gray-500 py-8">No contact submissions yet</p>
                ) : (
                  <>
                    {/* Desktop Table */}
                    <div className="hidden md:block">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Date</TableHead>
                            <TableHead>Name</TableHead>
                            <TableHead>Email</TableHead>
                            <TableHead>Phone</TableHead>
                            <TableHead>Status</TableHead>
                            <TableHead>Actions</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {contacts.map((contact) => (
                            <TableRow key={contact.id}>
                              <TableCell className="text-sm text-gray-500">
                                {formatDate(contact.created_at)}
                              </TableCell>
                              <TableCell className="font-medium">{contact.name}</TableCell>
                              <TableCell>
                                <a href={`mailto:${contact.email}`} className="text-sage-600 hover:underline">
                                  {contact.email}
                                </a>
                              </TableCell>
                              <TableCell>
                                {contact.phone && (
                                  <a href={`tel:${contact.phone}`} className="text-sage-600 hover:underline">
                                    {contact.phone}
                                  </a>
                                )}
                              </TableCell>
                              <TableCell>
                                <Select
                                  value={contact.status}
                                  onValueChange={(value) => handleStatusChange('contacts', contact.id, value)}
                                >
                                  <SelectTrigger className="w-32">
                                    <SelectValue>{getStatusBadge(contact.status)}</SelectValue>
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="new">New</SelectItem>
                                    <SelectItem value="contacted">Contacted</SelectItem>
                                    <SelectItem value="completed">Completed</SelectItem>
                                  </SelectContent>
                                </Select>
                              </TableCell>
                              <TableCell>
                                <div className="flex gap-2">
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => {
                                      setSelectedItem({ type: 'contact', data: contact });
                                      setShowDetailsDialog(true);
                                    }}
                                  >
                                    <Eye className="w-4 h-4" />
                                  </Button>
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    className="text-red-600 hover:bg-red-50"
                                    onClick={() => handleDelete('contacts', contact.id)}
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </Button>
                                </div>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                    
                    {/* Mobile Cards */}
                    <div className="md:hidden space-y-4">
                      {contacts.map((contact) => (
                        <div key={contact.id} className="bg-gray-50 rounded-lg p-4 space-y-3">
                          <div className="flex justify-between items-start">
                            <div>
                              <p className="font-medium text-gray-900">{contact.name}</p>
                              <p className="text-xs text-gray-500">{formatDate(contact.created_at)}</p>
                            </div>
                            {getStatusBadge(contact.status)}
                          </div>
                          <div className="space-y-1">
                            <a href={`mailto:${contact.email}`} className="text-sm text-sage-600 block">
                              {contact.email}
                            </a>
                            {contact.phone && (
                              <a href={`tel:${contact.phone}`} className="text-sm text-sage-600 block">
                                {contact.phone}
                              </a>
                            )}
                          </div>
                          <div className="flex gap-2 pt-2 border-t border-gray-200">
                            <Select
                              value={contact.status}
                              onValueChange={(value) => handleStatusChange('contacts', contact.id, value)}
                            >
                              <SelectTrigger className="flex-1">
                                <SelectValue placeholder="Status" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="new">New</SelectItem>
                                <SelectItem value="contacted">Contacted</SelectItem>
                                <SelectItem value="completed">Completed</SelectItem>
                              </SelectContent>
                            </Select>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                setSelectedItem({ type: 'contact', data: contact });
                                setShowDetailsDialog(true);
                              }}
                            >
                              <Eye className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              className="text-red-600 hover:bg-red-50"
                              onClick={() => handleDelete('contacts', contact.id)}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Scheduling Tab */}
          <TabsContent value="scheduling">
            <Card>
              <CardHeader className="pb-2 sm:pb-6">
                <CardTitle className="text-lg sm:text-xl">Scheduling Requests</CardTitle>
                <CardDescription>View and manage appointment requests</CardDescription>
              </CardHeader>
              <CardContent>
                {scheduling.length === 0 ? (
                  <p className="text-center text-gray-500 py-8">No scheduling requests yet</p>
                ) : (
                  <>
                    {/* Desktop Table */}
                    <div className="hidden md:block">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Date</TableHead>
                            <TableHead>Name</TableHead>
                            <TableHead>Email</TableHead>
                            <TableHead>Service</TableHead>
                            <TableHead>Contact Pref.</TableHead>
                            <TableHead>Status</TableHead>
                            <TableHead>Actions</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {scheduling.map((request) => (
                            <TableRow key={request.id}>
                              <TableCell className="text-sm text-gray-500">
                                {formatDate(request.created_at)}
                              </TableCell>
                              <TableCell className="font-medium">
                                {request.firstName} {request.lastName}
                              </TableCell>
                              <TableCell>
                                <a href={`mailto:${request.email}`} className="text-sage-600 hover:underline">
                                  {request.email}
                                </a>
                              </TableCell>
                              <TableCell className="capitalize">{request.serviceType}</TableCell>
                              <TableCell className="capitalize">{request.preferredContact}</TableCell>
                              <TableCell>
                                <Select
                                  value={request.status}
                                  onValueChange={(value) => handleStatusChange('scheduling', request.id, value)}
                                >
                                  <SelectTrigger className="w-32">
                                    <SelectValue>{getStatusBadge(request.status)}</SelectValue>
                                  </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="pending">Pending</SelectItem>
                                <SelectItem value="contacted">Contacted</SelectItem>
                                <SelectItem value="scheduled">Scheduled</SelectItem>
                                <SelectItem value="completed">Completed</SelectItem>
                                <SelectItem value="cancelled">Cancelled</SelectItem>
                              </SelectContent>
                            </Select>
                          </TableCell>
                          <TableCell>
                            <div className="flex gap-2">
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => {
                                  setSelectedItem({ type: 'scheduling', data: request });
                                  setShowDetailsDialog(true);
                                }}
                              >
                                <Eye className="w-4 h-4" />
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                className="text-red-600 hover:bg-red-50"
                                onClick={() => handleDelete('scheduling', request.id)}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                    </div>
                    
                    {/* Mobile Cards */}
                    <div className="md:hidden space-y-4">
                      {scheduling.map((request) => (
                        <div key={request.id} className="bg-gray-50 rounded-lg p-4 space-y-3">
                          <div className="flex justify-between items-start">
                            <div>
                              <p className="font-medium text-gray-900">{request.firstName} {request.lastName}</p>
                              <p className="text-xs text-gray-500">{formatDate(request.created_at)}</p>
                            </div>
                            {getStatusBadge(request.status)}
                          </div>
                          <div className="space-y-1">
                            <a href={`mailto:${request.email}`} className="text-sm text-sage-600 block">
                              {request.email}
                            </a>
                            <p className="text-sm text-gray-600">
                              <span className="capitalize">{request.serviceType}</span> · <span className="capitalize">{request.preferredContact}</span>
                            </p>
                          </div>
                          <div className="flex gap-2 pt-2 border-t border-gray-200">
                            <Select
                              value={request.status}
                              onValueChange={(value) => handleStatusChange('scheduling', request.id, value)}
                            >
                              <SelectTrigger className="flex-1">
                                <SelectValue placeholder="Status" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="pending">Pending</SelectItem>
                                <SelectItem value="contacted">Contacted</SelectItem>
                                <SelectItem value="scheduled">Scheduled</SelectItem>
                                <SelectItem value="completed">Completed</SelectItem>
                                <SelectItem value="cancelled">Cancelled</SelectItem>
                              </SelectContent>
                            </Select>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                setSelectedItem({ type: 'scheduling', data: request });
                                setShowDetailsDialog(true);
                              }}
                            >
                              <Eye className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              className="text-red-600 hover:bg-red-50"
                              onClick={() => handleDelete('scheduling', request.id)}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </main>

      {/* Details Dialog */}
      <Dialog open={showDetailsDialog} onOpenChange={setShowDetailsDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>
              {selectedItem?.type === 'contact' ? 'Contact Details' : 'Scheduling Request Details'}
            </DialogTitle>
          </DialogHeader>
          {selectedItem && (
            <div className="space-y-4">
              {selectedItem.type === 'contact' ? (
                <>
                  <div>
                    <Label className="text-gray-500">Name</Label>
                    <p className="font-medium">{selectedItem.data.name}</p>
                  </div>
                  <div>
                    <Label className="text-gray-500">Email</Label>
                    <p className="font-medium">{selectedItem.data.email}</p>
                  </div>
                  <div>
                    <Label className="text-gray-500">Phone</Label>
                    <p className="font-medium">{selectedItem.data.phone || 'Not provided'}</p>
                  </div>
                  <div>
                    <Label className="text-gray-500">Message</Label>
                    <p className="bg-gray-50 p-3 rounded-lg">{selectedItem.data.message}</p>
                  </div>
                  <div>
                    <Label className="text-gray-500">Submitted</Label>
                    <p className="font-medium">{formatDate(selectedItem.data.created_at)}</p>
                  </div>
                </>
              ) : (
                <>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-gray-500">First Name</Label>
                      <p className="font-medium">{selectedItem.data.firstName}</p>
                    </div>
                    <div>
                      <Label className="text-gray-500">Last Name</Label>
                      <p className="font-medium">{selectedItem.data.lastName}</p>
                    </div>
                  </div>
                  <div>
                    <Label className="text-gray-500">Email</Label>
                    <p className="font-medium">{selectedItem.data.email}</p>
                  </div>
                  <div>
                    <Label className="text-gray-500">Phone</Label>
                    <p className="font-medium">{selectedItem.data.phone}</p>
                  </div>
                  <div>
                    <Label className="text-gray-500">Service Type</Label>
                    <p className="font-medium capitalize">{selectedItem.data.serviceType}</p>
                  </div>
                  <div>
                    <Label className="text-gray-500">Preferred Contact Method</Label>
                    <p className="font-medium capitalize">{selectedItem.data.preferredContact}</p>
                  </div>
                  {selectedItem.data.message && (
                    <div>
                      <Label className="text-gray-500">Additional Notes</Label>
                      <p className="bg-gray-50 p-3 rounded-lg">{selectedItem.data.message}</p>
                    </div>
                  )}
                  <div>
                    <Label className="text-gray-500">Submitted</Label>
                    <p className="font-medium">{formatDate(selectedItem.data.created_at)}</p>
                  </div>
                </>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Admin;
